/* eslint-disable @typescript-eslint/naming-convention */
import type { Dayjs } from "dayjs";

import type { IAsset } from "pro-features/components/AutocompleteWithTabs/types";
import type {
    ICampaignLocationAndLanguage,
    ISupportedLanguagesAndLocale,
} from "app/components/campaigns/campaigns-wizard/CampaignsWizardConsts";
import type {
    ICampaignsBaseScrapingConfiguration,
    ICampaignsScrapingConfiguration,
} from "app/components/campaigns/shared/CampaignsConst";
import type { AssetUser } from "app/pages/digital-marketing/keywords-campaigns/edit-campaign/EditCampaignSettings.consts";
import type {
    ECampaignsLoadingState,
    ICampaignsConsumptionMetrics,
    IMyUserConsumptionMetrics,
    IRTUpdateCampaignKeywords,
} from "app/pages/digital-marketing/keywords-campaigns/infra/KeywordsCampaignTypes";
import type { DatesRange } from "app/pages/sales-intelligence/sub-modules/account-overview/shared/types/common";
import type { GranularityNames } from "app/UtilitiesAndConstants/Constants/Moment";

export enum EUpdateCampaignConfig {
    CREATE,
    DELETE,
    UPDATE,
}

export interface IBrandMonitoringCampaignsEmailConfigurations {
    email: string;
    frequency: GranularityNames;
    includeMainDomain: boolean;
}

export interface IBrandMonitoringCampaignsMetricsSummary {
    competitors: number;
    ads: number;
}

export interface IBrandMonitoringCampaignsMetricsStats {
    change: IBrandMonitoringCampaignsMetricsSummary;
    total: IBrandMonitoringCampaignsMetricsSummary;
}

export interface IBPCampaignBase {
    name: string;
    site: string;
    emailConfigurations: IBrandMonitoringCampaignsEmailConfigurations[];
    scrapingConfigurations: ICampaignsScrapingConfiguration[];
    isShared: boolean;
}
export interface IBPCampaignDataFromServer extends IBPCampaignBase {
    keywordCount: number;
    createdBy: string;
    stats?: IBrandMonitoringCampaignsMetricsStats | null;
    asset?: string;
    id: string;
    createdTime?: Dayjs;
    siteMetaData?: IAsset;
    updatedTime?: Dayjs;
    isActive?: boolean;
    scrapingConfigurations: ICampaignsScrapingConfiguration[];
    isEditable?: boolean;
    assetRolesSettings?: Record<"userAssetRoles", AssetUser[]>;
}

export type IAssetsList = Record<string, string>;

export interface IBPCampaignDataFromServerAndAssets {
    campaignConfigs: IBPCampaignDataFromServer[];
    assets: IAssetsList;
}
export interface IBPCurrentCampaignData extends IBPCampaignBase {
    keywordCount: number;
    createdBy: string;
    asset?: string;
    id: string;
    createdTime: Dayjs;
    updatedTime: Dayjs;
    isActive: boolean;
}

export interface IBrandMonitoringCampaigns {
    userCampaigns: IBPCampaignDataFromServer[];
    sharedCampaigns: IBPCampaignDataFromServer[];
}

export interface IBrandMonitoringCampaignsContext {
    isLoading: boolean;
    isError: ECampaignsLoadingState;
    currentCampaignConfiguration: IBPCampaignDataFromServer;
    campaignsConfigurations: IBPCampaignDataFromServer[];
    updateCampaignsFromServer: VoidFunction;
    languagesList: ISupportedLanguagesAndLocale[];
    locationsAndLanguagesLists: ICampaignLocationAndLanguage;
    durationObject: DatesRange;
    updateCampaignConfigurations: (
        operationType: EUpdateCampaignConfig,
        newConfiguration?: IBPCampaignDataFromServer,
        configurationId?: string,
    ) => void;
    campaignsConsumptionMetrics: ICampaignsConsumptionMetrics;
    myUserConsumptionMetrics: IMyUserConsumptionMetrics;
    updateCampaignsSubscriptions: (
        updatedCampaignId: string,
        emailConfigurations?: IBrandMonitoringCampaignsEmailConfigurations[],
    ) => void;
}

export interface IBrandMonitoringCampaignsProjectSummaryProps {
    id?: string;
    name: string;
    url: string;
    amountOfKeywords: number;
    amountOfSearchEngines: number;
    isHovered?: boolean;
    createdBy: string;
}

interface IMetricConfig {
    total: number;
    change: number;
    text: string;
    tooltip: string;
}

export interface IMetricSummariesConfig {
    competitors: IMetricConfig;
    ads: IMetricConfig;
}

export interface IBrandMonitoringCampaignsUpdateCampaignData {
    removedScrapingConfigurationGuids: string[];
    name: string;
    site: string;
    addedScrapingConfigurations?: ICampaignsBaseScrapingConfiguration[];
    emailConfigurations: IBrandMonitoringCampaignsEmailConfigurations[];
    keywordChanges?: IRTUpdateCampaignKeywords[];
    assetRolesSettings: Record<"userAssetRoles", AssetUser[]>;
}

export enum EBrandMonitoringCampaignsWizardPages {
    CREATE_PROJECT_STEP,
    SEARCH_ENGINES_STEP,
    KEYWORDS_STEP,
}
export enum BPCampaignsFields {
    SELECTED_SITE = "selectedSite",
    PROJECT_NAME = "projectName",
    EMAIL = "email",
    COMPETITORS = "competitors",
    KEYWORDS = "keywords",
    SEARCH_ENGINES = "searchEngines",
    LAST_SEARCH_ENGINE_ID = "lastSearchEngineID",
    AMOUNT_OF_USED_KEYWORDS = "amountOfUsedKeywordsCurrentCampaign",
}

export interface IBrandMonitoringCampaignsPageValidator {
    isPageHasErrors: () => boolean;
}
export interface IBrandMonitoringCampaignsCreateProjectProps {
    selectedSite?: IAsset;
}
